import { logAdminAction } from "./auditLog";
import { supabase } from "./supabase";
import type { Position } from "./positions";

export type InterviewOfferStatus = "extended" | "viewed" | "accepted" | "declined" | "withdrawn";

export type PipelineRow = {
  position_id: string;
  position_title: string;
  position_status: string;
  student_id: string;
  student_name: string | null;
  student_email: string | null;
  student_university: string | null;
  student_major: string | null;
  student_grad_year: number | null;
  weighted_score: number | null;
  category_breakdown: Record<string, number>;
  passed_gate: boolean | null;
  interest_opted_in_at: string;
  offer_status: InterviewOfferStatus | null;
  offer_id: string | null;
};

/**
 * Privacy-gated pipeline read. The RPC raises a 42501 error string
 * before judging closes — surface it to the UI so the gated empty
 * state can render. Returns [] for any permission issue.
 */
export async function getCompetitionPipeline(
  competitionId: string
): Promise<{ rows: PipelineRow[]; error: Error | null }> {
  const { data, error } = await supabase.rpc("get_competition_pipeline", {
    p_competition_id: competitionId,
  });
  if (error) return { rows: [], error: new Error(error.message) };
  return { rows: (data ?? []) as PipelineRow[], error: null };
}

export type InterviewOffer = {
  id: string;
  position_id: string;
  student_id: string;
  competition_id: string;
  company_id: string;
  extended_by: string;
  message: string;
  proposed_times: string | null;
  location_or_link: string | null;
  status: InterviewOfferStatus;
  extended_at: string;
  viewed_at: string | null;
  responded_at: string | null;
  response_message: string | null;
  deadline_at: string | null;
  created_at: string;
};

export type ExtendInterviewOfferInput = {
  positionId: string;
  studentId: string;
  competitionId: string;
  companyId: string;
  message: string;
  proposedTimes?: string | null;
  locationOrLink?: string | null;
  deadlineAt?: string | null;
};

export async function extendInterviewOffer(
  input: ExtendInterviewOfferInput
): Promise<{ id: string | null; error: Error | null }> {
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData?.user?.id ?? null;
  if (!uid) return { id: null, error: new Error("Not signed in") };

  const { data, error } = await supabase
    .from("interview_offers")
    .insert({
      position_id: input.positionId,
      student_id: input.studentId,
      competition_id: input.competitionId,
      company_id: input.companyId,
      extended_by: uid,
      message: input.message,
      proposed_times: input.proposedTimes ?? null,
      location_or_link: input.locationOrLink ?? null,
      deadline_at: input.deadlineAt ?? null,
    })
    .select("id")
    .single();

  if (error) {
    // The unique constraint message is technical-looking; soften for
    // UI consumption. Keeps the server-error surface honest while
    // hiding low-value error codes from end users.
    if ((error.code ?? "") === "23505") {
      return {
        id: null,
        error: new Error("You've already extended an offer to this candidate for this role."),
      };
    }
    return { id: null, error: new Error(error.message) };
  }
  void import("./analytics").then(({ track }) =>
    track("interview_offer_extended", {
      offer_id: data?.id as string,
      competition_id: input.competitionId,
      position_id: input.positionId,
    })
  );
  await logAdminAction({
    action: "interview_offer.extend",
    entityType: "interview_offers",
    entityId: (data?.id as string) ?? null,
    summary: `Extended interview offer`,
    diff: {
      after: {
        student_id: input.studentId,
        position_id: input.positionId,
        competition_id: input.competitionId,
        deadline_at: input.deadlineAt ?? null,
      },
    },
  });
  return { id: (data?.id as string) ?? null, error: null };
}

export async function withdrawInterviewOffer(offerId: string): Promise<{ error: Error | null }> {
  const { error } = await supabase.rpc("withdraw_interview_offer", { p_offer_id: offerId });
  if (error) return { error: new Error(error.message) };
  await logAdminAction({
    action: "interview_offer.withdraw",
    entityType: "interview_offers",
    entityId: offerId,
    summary: `Withdrew interview offer`,
    diff: { after: { status: "withdrawn" } },
  });
  return { error: null };
}

export async function markInterviewOfferViewed(offerId: string): Promise<{ error: Error | null }> {
  const { error } = await supabase.rpc("mark_interview_offer_viewed", { p_offer_id: offerId });
  return { error: error ? new Error(error.message) : null };
}

export async function respondToInterviewOffer(
  offerId: string,
  accept: boolean,
  responseMessage?: string | null
): Promise<{ error: Error | null }> {
  const { error } = await supabase.rpc("respond_to_interview_offer", {
    p_offer_id: offerId,
    p_accept: accept,
    p_response_message: responseMessage ?? null,
  });
  if (!error) {
    void import("./analytics").then(({ track }) =>
      track("interview_offer_responded", {
        offer_id: offerId,
        response: accept ? "accepted" : "declined",
      })
    );
  }
  return { error: error ? new Error(error.message) : null };
}

export async function countMyPendingInterviewOffers(): Promise<number> {
  const { data, error } = await supabase.rpc("count_my_pending_interview_offers");
  if (error) return 0;
  return typeof data === "number" ? data : 0;
}

// ─── Student offers list ───────────────────────────────────────────

export type StudentInterviewOffer = InterviewOffer & {
  position: Pick<Position, "id" | "title" | "location" | "comp_band" | "employment_type">;
  competition: { id: string; name: string };
  company: { id: string; name: string };
};

export async function listMyInterviewOffers(): Promise<StudentInterviewOffer[]> {
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData?.user?.id ?? null;
  if (!uid) return [];

  const { data: rows, error } = await supabase
    .from("interview_offers")
    .select(
      "*, position:positions(id, title, location, comp_band, employment_type), competition:competitions(id, name), company:companies(id, name)"
    )
    .eq("student_id", uid)
    .order("extended_at", { ascending: false });
  if (error || !rows) return [];

  return (rows as Array<
    InterviewOffer & {
      position:
        | Pick<Position, "id" | "title" | "location" | "comp_band" | "employment_type">
        | Pick<Position, "id" | "title" | "location" | "comp_band" | "employment_type">[]
        | null;
      competition: { id: string; name: string } | { id: string; name: string }[] | null;
      company: { id: string; name: string } | { id: string; name: string }[] | null;
    }
  >).map((r) => {
    const pos = Array.isArray(r.position) ? r.position[0] : r.position;
    const comp = Array.isArray(r.competition) ? r.competition[0] : r.competition;
    const co = Array.isArray(r.company) ? r.company[0] : r.company;
    return {
      ...r,
      position:
        pos ?? { id: r.position_id, title: "Position", location: null, comp_band: null, employment_type: null },
      competition: comp ?? { id: r.competition_id, name: "Competition" },
      company: co ?? { id: r.company_id, name: "Company" },
    };
  });
}
